import React, { createContext, useContext, useCallback } from "react";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useAuth } from "@/context/auth-provider";

type TourKey = "dashboard" | "transactions" | "categories" | "import";

interface TourProgress {
  completed: boolean;
  skipped: boolean;
}

interface TourState {
  dashboard: TourProgress;
  transactions: TourProgress;
  categories: TourProgress;
  import: TourProgress;
}

// Legacy state from previous architecture (dashboard, importLanding, importProgress)
interface PreviousTourState {
  dashboard?: TourProgress;
  importLanding?: TourProgress;
  importProgress?: TourProgress;
}

// Very old legacy state (original snake_case format)
interface LegacyTourState {
  dashboard_tour_completed?: boolean;
  dashboard_tour_skipped?: boolean;
  import_tour_completed?: boolean;
  import_tour_skipped?: boolean;
}

const defaultProgress = (): TourProgress => ({
  completed: false,
  skipped: false,
});

const getDefaultTourState = (): TourState => ({
  dashboard: defaultProgress(),
  transactions: defaultProgress(),
  categories: defaultProgress(),
  import: defaultProgress(),
});

const isModernTourState = (value: unknown): value is TourState => {
  if (!value || typeof value !== "object") return false;
  return (
    "dashboard" in value &&
    "transactions" in value &&
    "categories" in value &&
    "import" in value
  );
};

const isPreviousTourState = (value: unknown): value is PreviousTourState => {
  if (!value || typeof value !== "object") return false;
  return (
    "dashboard" in value &&
    "importLanding" in value &&
    "importProgress" in value
  );
};

const normalizeTourState = (
  value: TourState | PreviousTourState | LegacyTourState | null | undefined
): TourState => {
  // Handle modern state (current architecture)
  if (isModernTourState(value)) {
    return {
      dashboard: {
        completed: Boolean(value.dashboard?.completed),
        skipped: Boolean(value.dashboard?.skipped),
      },
      transactions: {
        completed: Boolean(value.transactions?.completed),
        skipped: Boolean(value.transactions?.skipped),
      },
      categories: {
        completed: Boolean(value.categories?.completed),
        skipped: Boolean(value.categories?.skipped),
      },
      import: {
        completed: Boolean(value.import?.completed),
        skipped: Boolean(value.import?.skipped),
      },
    };
  }

  // Handle previous state (dashboard, importLanding, importProgress)
  if (isPreviousTourState(value)) {
    // Consolidate importLanding and importProgress into single import key
    const importCompleted = Boolean(
      value.importLanding?.completed || value.importProgress?.completed
    );
    const importSkipped = Boolean(
      value.importLanding?.skipped || value.importProgress?.skipped
    );

    return {
      dashboard: {
        completed: Boolean(value.dashboard?.completed),
        skipped: Boolean(value.dashboard?.skipped),
      },
      transactions: defaultProgress(),
      categories: defaultProgress(),
      import: {
        completed: importCompleted,
        skipped: importSkipped,
      },
    };
  }

  // Handle very old legacy state (snake_case)
  if (value && typeof value === "object") {
    const legacy = value as LegacyTourState;
    return {
      dashboard: {
        completed: Boolean(legacy.dashboard_tour_completed),
        skipped: Boolean(legacy.dashboard_tour_skipped),
      },
      transactions: defaultProgress(),
      categories: defaultProgress(),
      import: {
        completed: Boolean(legacy.import_tour_completed),
        skipped: Boolean(legacy.import_tour_skipped),
      },
    };
  }

  return getDefaultTourState();
};

interface TourContextType {
  tourState: TourState;
  // Dashboard tour (Insights Dashboard)
  shouldShowDashboardTour: boolean;
  startDashboardTour: () => void;
  completeDashboardTour: () => void;
  skipDashboardTour: () => void;
  // Transactions tour
  shouldShowTransactionsTour: boolean;
  startTransactionsTour: () => void;
  completeTransactionsTour: () => void;
  skipTransactionsTour: () => void;
  // Categories tour
  shouldShowCategoriesTour: boolean;
  startCategoriesTour: () => void;
  completeCategoriesTour: () => void;
  skipCategoriesTour: () => void;
  // Import tour (consolidated)
  shouldShowImportTour: boolean;
  startImportTour: () => void;
  completeImportTour: () => void;
  skipImportTour: () => void;
  // Legacy handlers (backward compatibility - will be removed in Phase 5)
  shouldShowImportLandingTour: boolean;
  shouldShowImportProgressTour: boolean;
  startImportLandingTour: () => void;
  completeImportLandingTour: () => void;
  skipImportLandingTour: () => void;
  startImportProgressTour: () => void;
  completeImportProgressTour: () => void;
  skipImportProgressTour: () => void;
  // Utility
  resetTours: () => void;
}

const TourContext = createContext<TourContextType | null>(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};

interface TourProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const [storedState, setStoredState] = useLocalStorage<
    TourState | PreviousTourState | LegacyTourState
  >("tour_state", getDefaultTourState());

  const tourState = React.useMemo(
    () => normalizeTourState(storedState),
    [storedState]
  );

  React.useEffect(() => {
    if (!isModernTourState(storedState)) {
      setStoredState(tourState);
    }
  }, [storedState, setStoredState, tourState]);

  const updateTourProgress = useCallback(
    (key: TourKey, updates: Partial<TourProgress>) => {
      setStoredState((prev) => {
        const normalized = normalizeTourState(prev);
        return {
          ...normalized,
          [key]: {
            ...normalized[key],
            ...updates,
          },
        };
      });
    },
    [setStoredState]
  );

  const [activeTours, setActiveTours] = React.useState<Record<TourKey, boolean>>({
    dashboard: false,
    transactions: false,
    categories: false,
    import: false,
  });

  // Computed should-show states
  const shouldShowDashboardTour = activeTours.dashboard;
  const shouldShowTransactionsTour = activeTours.transactions;
  const shouldShowCategoriesTour = activeTours.categories;
  const shouldShowImportTour = activeTours.import;
  // Legacy (backward compatibility)
  const shouldShowImportLandingTour = activeTours.import;
  const shouldShowImportProgressTour = activeTours.import;

  // Dashboard tour handlers
  const startDashboardTour = useCallback(() => {
    if (!user) return;
    if (tourState.dashboard.completed || tourState.dashboard.skipped) {
      return;
    }
    setActiveTours((prev) =>
      prev.dashboard ? prev : { ...prev, dashboard: true }
    );
  }, [tourState.dashboard.completed, tourState.dashboard.skipped, user]);

  const completeDashboardTour = useCallback(() => {
    updateTourProgress("dashboard", { completed: true });
    setActiveTours((prev) => ({ ...prev, dashboard: false }));
  }, [updateTourProgress]);

  const skipDashboardTour = useCallback(() => {
    updateTourProgress("dashboard", { skipped: true });
    setActiveTours((prev) => ({ ...prev, dashboard: false }));
  }, [updateTourProgress]);

  // Transactions tour handlers
  const startTransactionsTour = useCallback(() => {
    if (!user) return;
    if (tourState.transactions.completed || tourState.transactions.skipped) {
      return;
    }
    setActiveTours((prev) =>
      prev.transactions ? prev : { ...prev, transactions: true }
    );
  }, [tourState.transactions.completed, tourState.transactions.skipped, user]);

  const completeTransactionsTour = useCallback(() => {
    updateTourProgress("transactions", { completed: true });
    setActiveTours((prev) => ({ ...prev, transactions: false }));
  }, [updateTourProgress]);

  const skipTransactionsTour = useCallback(() => {
    updateTourProgress("transactions", { skipped: true });
    setActiveTours((prev) => ({ ...prev, transactions: false }));
  }, [updateTourProgress]);

  // Categories tour handlers
  const startCategoriesTour = useCallback(() => {
    if (!user) return;
    if (tourState.categories.completed || tourState.categories.skipped) {
      return;
    }
    setActiveTours((prev) =>
      prev.categories ? prev : { ...prev, categories: true }
    );
  }, [tourState.categories.completed, tourState.categories.skipped, user]);

  const completeCategoriesTour = useCallback(() => {
    updateTourProgress("categories", { completed: true });
    setActiveTours((prev) => ({ ...prev, categories: false }));
  }, [updateTourProgress]);

  const skipCategoriesTour = useCallback(() => {
    updateTourProgress("categories", { skipped: true });
    setActiveTours((prev) => ({ ...prev, categories: false }));
  }, [updateTourProgress]);

  // Import tour handlers (consolidated)
  const startImportTour = useCallback(() => {
    if (!user) return;
    if (tourState.import.completed || tourState.import.skipped) {
      return;
    }
    setActiveTours((prev) =>
      prev.import ? prev : { ...prev, import: true }
    );
  }, [tourState.import.completed, tourState.import.skipped, user]);

  const completeImportTour = useCallback(() => {
    updateTourProgress("import", { completed: true });
    setActiveTours((prev) => ({ ...prev, import: false }));
  }, [updateTourProgress]);

  const skipImportTour = useCallback(() => {
    updateTourProgress("import", { skipped: true });
    setActiveTours((prev) => ({ ...prev, import: false }));
  }, [updateTourProgress]);

  // Legacy handlers (backward compatibility - delegate to import tour)
  const startImportLandingTour = useCallback(() => {
    startImportTour();
  }, [startImportTour]);

  const completeImportLandingTour = useCallback(() => {
    completeImportTour();
  }, [completeImportTour]);

  const skipImportLandingTour = useCallback(() => {
    skipImportTour();
  }, [skipImportTour]);

  const startImportProgressTour = useCallback(() => {
    startImportTour();
  }, [startImportTour]);

  const completeImportProgressTour = useCallback(() => {
    completeImportTour();
  }, [completeImportTour]);

  const skipImportProgressTour = useCallback(() => {
    skipImportTour();
  }, [skipImportTour]);

  // Utility handlers
  const resetTours = useCallback(() => {
    const resetState = getDefaultTourState();
    setStoredState(resetState);
    setActiveTours({
      dashboard: false,
      transactions: false,
      categories: false,
      import: false,
    });
  }, [setStoredState]);

  React.useEffect(() => {
    if (!user) {
      setActiveTours({
        dashboard: false,
        transactions: false,
        categories: false,
        import: false,
      });
    }
  }, [user]);

  return (
    <TourContext.Provider
      value={{
        tourState,
        // Dashboard tour
        shouldShowDashboardTour,
        startDashboardTour,
        completeDashboardTour,
        skipDashboardTour,
        // Transactions tour
        shouldShowTransactionsTour,
        startTransactionsTour,
        completeTransactionsTour,
        skipTransactionsTour,
        // Categories tour
        shouldShowCategoriesTour,
        startCategoriesTour,
        completeCategoriesTour,
        skipCategoriesTour,
        // Import tour
        shouldShowImportTour,
        startImportTour,
        completeImportTour,
        skipImportTour,
        // Legacy handlers (backward compatibility)
        shouldShowImportLandingTour,
        shouldShowImportProgressTour,
        startImportLandingTour,
        completeImportLandingTour,
        skipImportLandingTour,
        startImportProgressTour,
        completeImportProgressTour,
        skipImportProgressTour,
        // Utility
        resetTours,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
